import useServerPagination from '../../components/ui/useServerPagination';
import PaginatedTable from '../../components/ui/PaginatedTable';
import TableActions from '../../components/ui/TableActions';
import { useEffect, useState } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";

const API = "/api";
const money = (value) => `₹${Number(value || 0).toFixed(2)}`;
const escapeHtml = (value) =>
  String(value ?? "-").replace(
    /[&<>'"]/g,
    (char) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[
        char
      ],
  );

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [filters, setFilters] = useState({ search: "", status: "" });

  const request = async (path) => {
    const response = await fetch(`${API}${path}`, { credentials: "include" });
    const data = await response.json();
    if (!response.ok || data.status !== "success")
      throw new Error(data.message || "Request failed");
    return data;
  };

  const server=useServerPagination({endpoint:'/invoices/',onData:setInvoices,search:filters.search,filters:{status:filters.status}});
  const loadInvoices = async () => {server.reload();};

  useEffect(() => {
    loadInvoices();
  }, []);

  const openInvoice = async (id) => {
    setLoadingInvoice(true);
    try {
      const data = await request(`/invoices/${id}`);
      setSelectedInvoice({ ...data.invoice, id });
    } catch (error) {
      alert(error.message || "Failed to load invoice");
    } finally {
      setLoadingInvoice(false);
    }
  };

  const printInvoice = async (id) => {
    try {
      const invoice = (await request(`/invoices/${id}`)).invoice;
      const printWindow = window.open("", "_blank", "width=1000,height=800");
      if (!printWindow) {
        alert("Please allow pop-ups to print invoices.");
        return;
      }
      printWindow.document.write(invoicePrintHtml(invoice));
      printWindow.document.close();
      printWindow.focus();
      printWindow.onload = () => printWindow.print();
    } catch (error) {
      alert(error.message || "Failed to print invoice");
    }
  };

  const filtered = invoices;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-3xl font-serif">Invoices</h1>
            <p className="mt-1 text-sm text-gray-400">
              Generated automatically when the first sales-order payment is
              received.
            </p>
          </div>
          <div className="rounded-xl border border-amber-400/20 bg-amber-400/10 px-4 py-2 text-sm text-amber-300">
            {server.pagination.total} invoices
          </div>
        </header>
        <section className="grid gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 md:grid-cols-2">
          <input
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder="Search invoice, order or customer"
            className="rounded-xl border border-white/10 bg-black/30 px-4 py-2.5 text-sm outline-none focus:border-amber-400/70"
          />
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="rounded-xl border border-white/10 bg-black/30 px-4 py-2.5 text-sm outline-none focus:border-amber-400/70"
          >
            <option value="">All statuses</option>
            <option>Pending</option>
            <option>Partial</option>
            <option>Paid</option>
          </select>
        </section>
        <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#0b0b0f]">
          <PaginatedTable pagination={server.pagination} className="w-full min-w-[1050px] text-sm">
            <thead className="bg-black/40 text-left text-amber-400">
              <tr>
                {[
                  "Invoice",
                  "Customer",
                  "Order / branch",
                  "Total",
                  "Paid",
                  "Balance",
                  "Status",
                  "Actions",
                ].map((label) => (
                  <th key={label} className="p-4">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="8" className="p-10 text-center text-gray-500">
                    No invoices found.
                  </td>
                </tr>
              ) : (
                filtered.map((invoice) => (
                  <tr
                    key={invoice.id}
                    className="border-t border-white/10 hover:bg-white/5"
                  >
                    <td className="p-4">
                      <div className="font-semibold text-white">
                        {invoice.invoice_no}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        {invoice.invoice_date}
                      </div>
                    </td>
                    <td className="p-4">{invoice.customer}</td>
                    <td className="p-4">
                      <div>{invoice.order_no}</div>
                      <div className="mt-1 text-xs text-gray-500">
                        {invoice.branch || "-"}
                      </div>
                    </td>
                    <td className="p-4">{money(invoice.grand_total)}</td>
                    <td className="p-4 text-emerald-400">
                      {money(invoice.paid_amount)}
                    </td>
                    <td className="p-4 text-red-300">
                      {money(invoice.balance_amount)}
                    </td>
                    <td className="p-4">
                      <Status status={invoice.status} />
                    </td>
                    <td className="p-4"><TableActions>
                      <div className="flex gap-3">
                        <button
                          onClick={() => openInvoice(invoice.id)}
                          className="font-medium text-sky-400 hover:text-sky-300"
                        >
                          View
                        </button>
                        <button
                          onClick={() => printInvoice(invoice.id)}
                          className="font-medium text-emerald-400 hover:text-emerald-300"
                        >
                          Print
                        </button>
                      </div>
                    </TableActions></td>
                  </tr>
                ))
              )}
            </tbody>
          </PaginatedTable>
        </div>
        {loadingInvoice && (
          <div className="fixed inset-0 z-50 grid place-items-center bg-black/70">
            <div className="rounded-xl border border-white/10 bg-[#101014] px-5 py-4 text-sm">
              Loading invoice…
            </div>
          </div>
        )}
        {selectedInvoice && (
          <InvoiceModal
            invoice={selectedInvoice}
            onClose={() => setSelectedInvoice(null)}
            onPrint={() => printInvoice(selectedInvoice.id)}
          />
        )}
      </div>
    </DashboardLayout>
  );
}

function Status({ status }) {
  const style =
    status === "Paid"
      ? "bg-emerald-400/15 text-emerald-300"
      : status === "Partial"
        ? "bg-sky-400/15 text-sky-300"
        : "bg-amber-400/15 text-amber-300";
  return (
    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${style}`}>
      {status}
    </span>
  );
}

function InvoiceModal({ invoice, onClose, onPrint }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-5xl overflow-hidden rounded-3xl border border-white/10 bg-[#101014] shadow-2xl">
        <header className="flex items-start justify-between border-b border-white/10 bg-gradient-to-r from-amber-400/10 via-transparent px-6 py-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-400">
              Jaggi Sweets
            </p>
            <h2 className="mt-1 text-2xl font-semibold">
              {invoice.invoice_no}
            </h2>
            <p className="mt-1 text-sm text-gray-400">
              Invoice date: {invoice.invoice_date}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-xl text-gray-400 hover:bg-red-500/10 hover:text-red-300"
          >
            ×
          </button>
        </header>
        <div className="max-h-[76vh] overflow-y-auto p-6">
          <div className="grid gap-4 md:grid-cols-3">
            <Details
              label="Billed to"
              value={invoice.customer?.name}
              detail={invoice.customer?.mobile || invoice.customer?.email}
            />
            <Details
              label="GSTIN"
              value={invoice.customer?.gst_no || "Not provided"}
            />
            <Details
              label="Order"
              value={invoice.order?.order_no}
              detail={invoice.order?.order_type}
            />
          </div>
          <div className="mt-6 overflow-x-auto rounded-xl border border-white/10">
            <PaginatedTable className="w-full min-w-[600px] text-sm">
              <thead className="bg-black/40 text-left text-gray-400">
                <tr>
                  <th className="p-3">Item</th>
                  <th>Qty</th>
                  <th>Rate</th>
                  <th className="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((item, index) => (
                  <tr key={index} className="border-t border-white/10">
                    <td className="p-3">{item.item_name}</td>
                    <td>{item.qty}</td>
                    <td>{money(item.rate)}</td>
                    <td className="pr-3 text-right">{money(item.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </PaginatedTable>
          </div>
          <div className="mt-6 ml-auto max-w-sm space-y-2 rounded-xl border border-white/10 bg-black/20 p-4 text-sm">
            <Total label="Subtotal" value={invoice.subtotal} />
            <Total label="Discount" value={invoice.discount} />
            <Total label="GST" value={invoice.tax} />
            <hr className="border-white/10" />
            <Total label="Grand total" value={invoice.grand_total} strong />
            <Total
              label="Paid"
              value={invoice.paid_amount}
              color="text-emerald-300"
            />
            <Total
              label="Balance"
              value={invoice.balance_amount}
              color="text-red-300"
            />
          </div>
          <h3 className="mt-7 font-semibold text-amber-300">Payment history</h3>
          <div className="mt-3 overflow-x-auto rounded-xl border border-white/10">
            <PaginatedTable className="w-full min-w-[600px] text-sm">
              <thead className="bg-black/40 text-left text-gray-400">
                <tr>
                  <th className="p-3">Date</th>
                  <th>Mode</th>
                  <th>Remarks</th>
                  <th className="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.payments.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="p-5 text-center text-gray-500">
                      No payments recorded.
                    </td>
                  </tr>
                ) : (
                  invoice.payments.map((payment, index) => (
                    <tr key={index} className="border-t border-white/10">
                      <td className="p-3">
                        {new Date(payment.date).toLocaleString("en-IN")}
                      </td>
                      <td>{payment.payment_mode}</td>
                      <td>{payment.remarks || "-"}</td>
                      <td className="pr-3 text-right text-emerald-300">
                        {money(payment.amount)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </PaginatedTable>
          </div>
        </div>
        <footer className="flex justify-end gap-3 border-t border-white/10 px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-xl border border-white/20 px-4 py-2 text-sm"
          >
            Close
          </button>
          <button
            onClick={onPrint}
            className="rounded-xl bg-emerald-400 px-5 py-2 text-sm font-semibold text-black"
          >
            Print invoice
          </button>
        </footer>
      </div>
    </div>
  );
}

function Details({ label, value, detail }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-4">
      <div className="text-xs uppercase tracking-wider text-gray-500">
        {label}
      </div>
      <div className="mt-1 font-medium">{value || "-"}</div>
      {detail && <div className="mt-1 text-sm text-gray-400">{detail}</div>}
    </div>
  );
}
function Total({ label, value, strong = false, color = "" }) {
  return (
    <div
      className={`flex justify-between ${strong ? "font-bold text-amber-300" : color}`}
    >
      <span>{label}</span>
      <span>{money(value)}</span>
    </div>
  );
}

function invoicePrintHtml(invoice) {
  const items = invoice.items
    .map(
      (item) =>
        `<tr><td>${escapeHtml(item.item_name)}</td><td>${escapeHtml(item.qty)}</td><td>${money(item.rate)}</td><td>${money(item.amount)}</td></tr>`,
    )
    .join("");
  return `<!doctype html><html><head><title>${escapeHtml(invoice.invoice_no)}</title><style>body{font-family:Arial,sans-serif;color:#161616;margin:36px}header{display:flex;justify-content:space-between;border-bottom:2px solid #c99100;padding-bottom:18px}h1{margin:0;color:#8a6200}table{width:100%;border-collapse:collapse;margin-top:25px}th,td{padding:10px;border-bottom:1px solid #ddd;text-align:left}th{background:#f5ead0}.right{text-align:right}.summary{margin:24px 0 0 auto;width:320px}.summary div{display:flex;justify-content:space-between;padding:6px 0}.total{font-weight:bold;border-top:2px solid #222;margin-top:5px;padding-top:9px!important}@media print{body{margin:15mm}}</style></head><body><header><div><h1>Jaggi Sweets</h1><p>Sales Invoice</p></div><div><strong>${escapeHtml(invoice.invoice_no)}</strong><br/>Date: ${escapeHtml(invoice.invoice_date)}<br/>Order: ${escapeHtml(invoice.order?.order_no)}</div></header><section><h3>Bill To</h3><strong>${escapeHtml(invoice.customer?.name)}</strong><br/>GSTIN: ${escapeHtml(invoice.customer?.gst_no || "Not provided")}<br/>${escapeHtml(invoice.customer?.address || "")}</section><table><thead><tr><th>Item</th><th>Qty</th><th>Rate</th><th class="right">Amount</th></tr></thead><tbody>${items}</tbody></table><div class="summary"><div><span>Subtotal</span><span>${money(invoice.subtotal)}</span></div><div><span>Discount</span><span>${money(invoice.discount)}</span></div><div><span>GST</span><span>${money(invoice.tax)}</span></div><div class="total"><span>Grand Total</span><span>${money(invoice.grand_total)}</span></div><div><span>Paid</span><span>${money(invoice.paid_amount)}</span></div><div><span>Balance</span><span>${money(invoice.balance_amount)}</span></div></div></body></html>`;
}
